import { Box, Flex, Link, Text, VStack } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { MfaMethod } from 'src/contexts/AuthContext';

type MfaSelectProps = {
  allowedTypes: MfaMethod[] | null;
  onSelect: (type: MfaMethod) => Promise<void>;
  onCancel: () => void;
};

/**
 * Shown when a user has more than one MFA method registered.
 * The email code is only sent after the user picks the email option.
 */
export const MfaSelect: FC<MfaSelectProps> = ({
  allowedTypes,
  onSelect,
  onCancel,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingType, setLoadingType] = useState<MfaMethod | null>(null);

  const types = allowedTypes ?? ['EMAIL', 'TOTP'];

  const handleSelect = async (type: MfaMethod) => {
    try {
      setErrorMessage(null);
      setLoadingType(type);
      await onSelect(type);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      }
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <Box>
      <Text mb={6}>Choose how you want to verify your identity:</Text>
      <VStack spacing={4} align="stretch">
        {types.includes('TOTP') && (
          <Button
            buttonName="mfa-select-totp"
            variant="primary"
            width="100%"
            isLoading={loadingType === 'TOTP'}
            isDisabled={loadingType !== null}
            onClick={() => handleSelect('TOTP')}>
            Use authenticator app
          </Button>
        )}
        {types.includes('EMAIL') && (
          <Button
            buttonName="mfa-select-email"
            variant="outline"
            width="100%"
            isLoading={loadingType === 'EMAIL'}
            isDisabled={loadingType !== null}
            onClick={() => handleSelect('EMAIL')}>
            Email me a code
          </Button>
        )}
      </VStack>
      {errorMessage && <ErrorNotifBox title={errorMessage} />}
      <Flex direction="row" justifyContent="center" mt={6}>
        <Link
          fontFamily="marketingPage"
          fontSize="16px"
          fontWeight="semibold"
          onClick={onCancel}
          color="gray.500"
          _hover={{ textDecoration: 'underline' }}>
          Back to Login
        </Link>
      </Flex>
    </Box>
  );
};
